import loadOrdersCustom from "@bToC/Handlers/LoadOrdersCustom";
import updateSearchFieldsCustom from "@bToC/Handlers/updateSearchFieldsCustom";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getOrdersDataView, OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import setVmiOrderHistoryPage from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/SetVmiOrderHistoryPage";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// ✅ Use your extended AllowedContexts type
import { AllowedContexts } from "@bToC/Types/ExtendedAllowedContexts";
import { DoNotFollowLinks } from "@insite/client-framework/Types/FieldDefinition";

const mapStateToProps = (state: ApplicationState) => ({
    settings: getSettingsCollection(state),
    ordersDataView: getOrdersDataView(state, state.pages.orderHistory.getOrdersParameter),
    getOrdersParameter: state.pages.orderHistory.getOrdersParameter,
    shouldLoadOrderStatusMappings: !getOrderStatusMappingDataView(state).value,
    location: getLocation(state),
});

const mapDispatchToProps = {
    loadOrdersCustom,
    loadOrderStatusMappings,
    updateSearchFieldsCustom,
    setVmiOrderHistoryPage,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const QuoteHistoryPage = ({
    settings,
    loadOrdersCustom,
    loadOrderStatusMappings,
    updateSearchFieldsCustom,
    setVmiOrderHistoryPage,
    ordersDataView,
    id,
    shouldLoadOrderStatusMappings,
    history,
    location,
    getOrdersParameter,
}: Props) => {
    let firstLoad = false;

    useEffect(() => {
        setVmiOrderHistoryPage(false);
        const showQuotesInOrderHistoryTable = getCookie("showQuotesInOrderHistoryTable") ?? "Yes";
        firstLoad = true;
        const pageSizeCookie = getCookie("OrderHistory-PageSize");
        const pageSize = pageSizeCookie ? parseInt(pageSizeCookie, 10) : undefined;

        if (location.search) {
            const getOrdersApiParameter = parseQueryString<GetOrdersApiParameter>(location.search);
            if (pageSize) {
                getOrdersApiParameter.pageSize = pageSize;
            }
            updateSearchFieldsCustom({
                ...getOrdersApiParameter,
                type: "Replace",
                showQuotes: showQuotesInOrderHistoryTable,
            });
        } else if (settings.orderSettings.lookBackDays > 0) {
            const fromDate = new Date(Date.now() - settings.orderSettings.lookBackDays * 60 * 60 * 24 * 1000);
            updateSearchFieldsCustom({
                fromDate: convertDateToDateOnlyString(fromDate),
                type: "Initialize",
                pageSize,
                showQuotes: showQuotesInOrderHistoryTable,
            });
        } else {
            updateSearchFieldsCustom({ type: "Initialize", pageSize, showQuotes: showQuotesInOrderHistoryTable });
        }

        if (shouldLoadOrderStatusMappings) {
            loadOrderStatusMappings();
        }
    }, []);

    useEffect(() => {
        if (!firstLoad) {
            const queryString = qs.stringify(getOrdersParameter);
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [getOrdersParameter]);

    useEffect(() => {
        const showQuotesInOrderHistoryTable = getCookie("showQuotesInOrderHistoryTable") ?? "Yes";
        if (!ordersDataView.value && !ordersDataView.isLoading) {
            loadOrdersCustom({ ...getOrdersParameter, showQuotes: showQuotesInOrderHistoryTable });
        } else if (!ordersDataView.isLoading && ordersDataView.value?.length === 0 && getOrdersParameter.page) {
            updateSearchFieldsCustom({ page: getOrdersParameter.page - 1, showQuotes: showQuotesInOrderHistoryTable });
        }
    });

    return (
        <Page
            data-test-selector="quoteHistory"
            css={css`
                position: relative;
            `}
        >
            <OrdersDataViewContext.Provider value={ordersDataView}>
                <Zone contentId={id} zoneName="Content" />
                <Modals skipInventoryValidation={true} skipProductPrice={true} />
            </OrdersDataViewContext.Provider>
        </Page>
    );
};

const pageModule: PageModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(QuoteHistoryPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
        fieldDefinitions: [DoNotFollowLinks],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "QuoteHistoryPage" instead of this constant.
 */
export const QuoteHistoryPageContext: AllowedContexts = "QuoteHistoryPage";
